import * as Sentry from '@sentry/react';
import WebApp from '@twa-dev/sdk';
import { AxiosError } from 'axios';

import { copyToClipboard } from '@/shared/helpers/copy-to-clipboard';
import { getDeviceInfo } from '@/shared/helpers/environment/get-device-info';
import {
  getCurrentSRC,
  getHeight,
  getHostname,
  getWidth,
} from '@/shared/helpers/environment/helpers';
import { getAndroidInfo } from '@/shared/helpers/get-android-info';
import { getErrorMessage } from '@/shared/helpers/get-error-message';
import {
  LogDevMessageKinds,
  makeLogDevMessage,
} from '@/shared/helpers/make-log-dev-message';
import { showPopup } from '@/shared/helpers/show-popup';
import { getFeatureFlag } from '@/shared/hooks/use-feature-flag';
import { useAppStateRaw } from '@/shared/store/app-store/app-store';

import { LIB_NAME } from '../service-logger';
import { type ServiceEvent, ServiceEventType } from '../types';

import packg from '../../../../../package.json';

const logErrorInDevMode = makeLogDevMessage<ServiceEvent>(
  LogDevMessageKinds.error,
  (payload) => payload.message,
  ['EVENT', (event) => event]
);

export const logErrorMini = async (
  error: string | Error | AxiosError,
  place: string,
  extra: Record<string, any> = {}
) => {
  const { isKassir } = getFeatureFlag();
  const deviceInfo = getDeviceInfo(WebApp?.platform);

  const payload: ServiceEvent = {
    type: ServiceEventType.ERROR,
    message: getErrorMessage(error),
    stacktrace: (typeof error === 'object' ? error : {})?.stack ?? '',
    tags: {
      d_mod: deviceInfo.device_model,
      d_os: deviceInfo.device_os,
      d_p: deviceInfo.device_platform,
      tg_id: String(WebApp?.initDataUnsafe?.user?.id) ?? 'undefined',
      lib_n: LIB_NAME,
      lib_v: packg.version,
    },
    extra: {
      loc: useAppStateRaw.getState().lang,
      hn: getHostname(),
      br_w: getWidth(),
      br_h: getHeight(),
      src: getCurrentSRC(),
      place: place,
      time: new Date().toISOString(),
      ...(error instanceof AxiosError && {
        r_url: error.config?.url,
        r_data: error.config?.data,
      }),
      ...extra,
      ...(getAndroidInfo() ?? {}),
    },
  };

  if (!import.meta.env.DEV) {
    Sentry.withScope((scope) => {
      Object.entries(payload.tags).forEach(([key, value]) => {
        scope.setTag(key, value);
      });

      scope.setExtras(payload.extra);

      if (error instanceof Error) {
        Sentry.captureException(error);
      } else {
        Sentry.captureMessage(payload.message);
      }
    });
  }

  if (isKassir && !import.meta.env.DEV) {
    const formattedMessage = `
      Error: ${payload.message}
      Stack: ${payload.stacktrace}
      Place: ${place}
      Time: ${payload.extra.time}
      Device: ${payload.tags.d_mod} ${payload.tags.d_os} ${payload.tags.d_p}
      ${Object.entries(extra)
        .map(([key, value]) => `${key}: ${value}`)
        .join('\n')}
    `.trim();

    try {
      showPopup(
        {
          title: 'Debug Error Info',
          message: payload.message,
          buttons: [
            { type: 'close', id: 'close' },
            { type: 'default', id: 'copy', text: 'Copy' },
          ],
        },
        (buttonId) => {
          if (buttonId === 'copy') {
            copyToClipboard(JSON.stringify(payload, null, '  ')).catch((err) => {
              showPopup({ message: err.message });
              console.error(err);
            });
          }
        }
      );
    } catch (e) {
      alert(formattedMessage);
    }
  }

  logErrorInDevMode(payload);
};
